import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, Observable, take } from 'rxjs';
import { LayerService } from '../../../../services/layer.service';
import {
  closestUpperDivisibleBy5,
  formatLegend,
  get4kmBufferExtent,
  getColormapParams,
  getDiscreteColors,
  getGradientBackground,
  getSpacedNumericLegends, isLinearLayer,
  isNumeric
} from '../../../../utils';
import { Layer } from '../../../../models/layer.models';
import { DomSanitizer } from '@angular/platform-browser';
import { ChartOptions } from '../../../report/report-area-details/report-area-details-modal.component';
import { ApexXAxis, ChartComponent } from 'ng-apexcharts';
import * as chroma from 'chroma.ts';
import { FeatureGeometry } from '../../../../models/area-item.model';
import { Project } from '../../../../models/project.module';
import { IndexedDbService } from '../../../../services/dbService.service';
import { SaveAreaDialogComponent } from '../save-area-dialog/save-area-dialog.component';
import { NewReportService } from '../../../../services/newReport.service';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatButton } from '@angular/material/button';
import { KeyValuePipe } from '@angular/common';
import { RoundPipe } from '../../../../pipes/round.pipe';

interface DataLayerStats {
  colors?: string[];
  layer: Layer,
  data?: any,
  histData?: any,
  value?: any,
  valueLabel?: any,
  histogram?: any,
  xaxis?: any,
  yaxis?: any,
  tooltip?: any,
  categories?: any,
  fill?: any,
  title?: any,
  img?: any,
  img_blob?: any,
  plotOptions?: any,
  categorical?: boolean,
  status: boolean,
}

@Component({
  selector: 'frmg-quick-area-dialog',
  templateUrl: './area-quick-report-dialog.component.html',
  styleUrls: ['./area-quick-report-dialog.component.scss'],
  imports: [MatTabGroup, MatTab, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, ChartComponent, MatButton, MatDialogClose, KeyValuePipe, RoundPipe]
})
export class AreaQuickReportDialogComponent implements OnInit {
  protected readonly getDiscreteColors = getDiscreteColors;
  protected readonly getGradientBackground = getGradientBackground;
  protected readonly isNumeric = isNumeric;
  protected readonly formatLegend = formatLegend;
  protected readonly getSpacedNumericLegends = getSpacedNumericLegends;

  @ViewChild('chartAPXExp', { static: false }) chartAPXExp: ChartComponent;

  private layerTabsOrder = ['all', 'Fuel Type', 'Fuel Hazard', 'PCBA', 'PSBA', 'Canopy Height', 'Canopy Height Model', 'Exposure', 'Water'];

  public activeLayer: string = 'all';
  public chartOptions: Partial<ChartOptions>;
  public layers: string[] = ['all'];
  public displayedColumns = ['layer', 'min', 'max', 'mean', 'std'];
  public selectIndex = 0;
  public chartIndex = 0;

  public layersObj: {
    [key: string]: DataLayerStats
  } = {
      all: {
        // @ts-ignore
        layer: { layer: { name: 'all' } },
        status: true
      }
    };

  public layerData: DataLayerStats[] = [];

  constructor(
    private dialogRef: MatDialogRef<AreaQuickReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      layer: FeatureGeometry,
      project: Project
    },
    private indexedDbService: IndexedDbService,
    private reportService: NewReportService,
    private layerService: LayerService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer) {
    this.chartOptions = {
      series: [],
      chart: {
        animations: {
          enabled: false
        },
        type: 'bar',
        toolbar: {
          show: false
        },
        height: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true
      },
      dataLabels: {
        enabled: false
      },
      noData: {
        text: 'No Data'
      },
      tooltip: {
        enabled: true,
        x: {
          show: false
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const initData = series[seriesIndex][dataPointIndex];
          return `<div style="width: 100%; height: 100%; padding: 10px 12px;background: #F7F9F9; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18); border-radius: 4px; display: flex">
            <div style="color: #272727; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 500;">${w.globals.categoryLabels[dataPointIndex]}:</div>
            <div style="color: #5E84F7; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 600; margin-left: 16px">${initData} %</div>
           </div>`;
        },
        y: {
          formatter(val: number): string {
            return `${val}%`;
          }
        }
      },
      states: {
        active: {
          filter: {
            type: 'none' /* none, lighten, darken */
          }
        },
        hover: {
          filter: {
            type: 'none'
          }
        }
      }
    };
    this.data.layer.coordinates = this.data.layer.coordinates.map(cor => +cor);
  }


  ngOnInit(): void {
    this.indexedDbService.getData(`${this.data.layer.coordinates[0]}, ${this.data.layer.coordinates[1]}`, 'skyforest', 'quickReport').subscribe(resp => {
      const newObj = resp;
      Object.keys(newObj).forEach(layer => {
        if (newObj[layer].img_blob) {
          delete newObj[layer].img;
          newObj[layer].img = newObj[layer].img_blob;
          newObj[layer].colors = this.getFillColors(newObj[layer].layer, newObj[layer].categories.length);
          newObj[layer].fill = {
            colors: [function(value) {
              return value.w.config.colors[value.dataPointIndex];
            }],
            opacity: 1
          };
        }
        if (newObj[layer].categorical === false) {
          newObj[layer].xaxis.labels.formatter = x => {
            return newObj[layer].categories[x] || closestUpperDivisibleBy5(newObj[layer].data.max);
          };
          newObj[layer].xaxis.min = 0;
        }
        // if (layer === 'landcover') {
        // newObj[layer].xaxis.labels.formatter = (x)=>{return newObj[layer].categories[x]};
        // }
        newObj[layer].yaxis = this.getYaxisConfig(newObj[layer]);
        newObj[layer].tooltip = this.getTooltipConfig(newObj[layer]);
        this.layerData = [...this.layerData, newObj[layer]];
      });
      this.layersObj = newObj;
    }, _ => {
      const bbox = get4kmBufferExtent(this.data.layer.coordinates[0], this.data.layer.coordinates[1]);
      this.layerService.getLayers().pipe(debounceTime(0), take(1)).subscribe(layers => {
        layers.forEach(layer => {
          // @ts-ignore
          if ((layer.use_in_reports || layer.layer.name == 'exposure')) {
            this.layers.push(layer.layer.name);
            this.layersObj[layer.layer.name] = { layer, status: false };
            const params = getColormapParams(layer, layer.layer.value_type === 'CATEGORICAL');
            const bins = `${Object.keys(layer.layer.colormap_values).length}`;
            const categorical = layer.layer.value_type == 'CATEGORICAL';
            this.layersObj[layer.layer.name].categorical = categorical;
            this.reportService.getAreaQuickReportLayerInfo(layer.layer.tif_path, bbox, bins, categorical).subscribe(resp => {
              this.layersObj[layer.layer.name].data = resp.b1;
              const total = resp.b1.histogram[0].reduce((sum, val) => sum + val, 0);
              const percentages = total === 0
                ? resp.b1.histogram[0].map(() => 0)
                : resp.b1.histogram[0].map(val => (val / total) * 100);
              this.layersObj[layer.layer.name].data.histogram[0] = percentages;
              if (categorical) {
                this.layersObj[layer.layer.name].histData = percentages.map((histNumber: number) => {
                  return +histNumber.toFixed(1);
                });
                if (layer.layer.name === 'landcover') this.layersObj[layer.layer.name].histData.pop();
              } else {
                this.layersObj[layer.layer.name].histData = Array.from({ length: percentages.length }, (_, i) => ({
                  x: i + 0.5,
                  y: +percentages[i].toFixed(1)
                }));
              }
              let categories = resp.b1.histogram[1];
              if (categorical) {
                categories = categories.map(el => {
                  if (layer.layer.name === 'landcover') el = el - 1;
                  return getDiscreteColors(layer)[el];
                });
                if (layer.layer.name === 'landcover') {
                  categories.pop();
                  // categories = categories.map((str: string)=>{
                  //   if (!str) return str;
                  //   const array = str.split(' ');
                  //   if (array.length > 1) {
                  //     return array.map((word: string, index: number)=>{
                  //       if (index === 0) return word;
                  //       return word.slice(0, 5) + ".."
                  //     }).join(' ');
                  //   } else {
                  //     return array[0]
                  //   }
                  // })
                }
              } else {
                categories = categories.map(el => el.toFixed(0));
              }
              this.layersObj[layer.layer.name].categories = categories;
              this.layersObj[layer.layer.name].colors = this.getFillColors(layer, categories.length);
              this.layersObj[layer.layer.name].fill = {
                colors: this.getFillColors(layer, categories.length),
                opacity: 1
              };
              this.layersObj[layer.layer.name].title = {
                text: layer.layer.name,
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif'
                },
                align: 'center'
              };
              const xaxis: ApexXAxis = {
                categories: categorical ? categories : [],
                tickAmount: categorical ? categories.length : percentages.length,
                // tickPlacement: "on",
                // min: 0,
                title: {
                  text: layer.layer.name + (layer?.layer.unit?.length ? ` (${layer.layer.unit})` : ''),
                  style: {
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 500,
                    fontSize: '13px'
                  }
                },
                max: categorical ? categories.length : percentages.length,
                tickPlacement: 'on',
                labels: {
                  style: {
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 500,
                    fontSize: '12px',
                    colors: '#272727'
                  }
                }
              };
              if (layer.layer.name === 'landcover') {
                xaxis.labels.rotate = -45;
                xaxis.labels.rotateAlways = true;
                xaxis.categories = xaxis.categories.map(el => el ? el.split(' ').map((word, index) => index !== 0 && word.length > 5 ? `${word.slice(0, 3)}..` : word).join(' ') : el);
                // xaxis.labels.formatter = (x)=>{return this.layersObj[layer.layer_name].categories[x]};
              }
              if (!categorical) {
                // @ts-ignore
                xaxis.labels.formatter = (x) => {
                  return this.layersObj[layer.layer.name].categories[x] || closestUpperDivisibleBy5(this.layersObj[layer.layer.name].data.max);
                };
                xaxis.min = 0;
              }
              this.layersObj[layer.layer.name].xaxis = xaxis;
              this.layersObj[layer.layer.name].histogram =
                [{
                  name: layer.layer.name,
                  data: this.layersObj[layer.layer.name].histData
                }];
              this.layersObj[layer.layer.name].plotOptions = {
                title: {
                  align: 'center'
                },
                bar: {
                  borderRadius: 5,
                  borderRadiusApplication: 'end',
                  columnWidth: categorical ? '90%' : '100%'
                }
              };
              this.layersObj[layer.layer.name].yaxis = this.getYaxisConfig(this.layersObj[layer.layer.name]);
              this.layersObj[layer.layer.name].tooltip = this.getTooltipConfig(this.layersObj[layer.layer.name]);
              this.layerData = [...this.layerData, this.layersObj[layer.layer.name]];
              this.checkIfAllDataDownloaded(layer);
            });
            if (layer.layer.name !== 'exposure') {
              this.reportService.getAreaQuickReportLayerImages(layer.layer.tif_path, bbox, params).subscribe(resp => {
                this.layersObj[layer.layer.name].img = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(resp));
                this.blobToBase64(resp).subscribe(str => {
                  this.layersObj[layer.layer.name].img_blob = str;
                  this.checkIfAllDataDownloaded(layer);
                });
              });
            } else {
              this.layersObj[layer.layer.name].img = 'skip';
            }
            this.reportService.getAreaQuickReportLayerPointInfo(layer.layer.tif_path, [+this.data.layer.coordinates[0], +this.data.layer.coordinates[1]]).subscribe(resp => {
              this.layersObj[layer.layer.name].value = resp.values[0];
              this.checkIfAllDataDownloaded(layer);
            });
          }
        });
      });
    });
  };


  public forceRedrawChart(): void {
    this.cd.detectChanges();
    this.chartIndex = this.selectIndex;
  }

  public getYaxisConfig(layer: any) {
    return {
      opposite: true,
      tickAmount: 5,
      max: layer.layer.layer_name == 'all' ? 100 : closestUpperDivisibleBy5(Math.max(...layer.histData.map(data => data.y !== undefined ? data.y : data))),
      min: 0,
      title: {
        text: 'Percentage (%)',
        style: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500,
          fontSize: '14px'
        }
      },
      labels: {
        style: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500,
          fontSize: '10px',
          colors: '#272727'
        },
        formatter(val: number): string | string[] {
          if (val / 1000 > 1) {
            return `${(val / 1000).toFixed(0)}k`;
          }
          return `${val}`;
        }
      }
    };
  }

  public getTooltipConfig(layer: any) {
    return {
      enabled: true,
      x: {
        show: false
      },
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const initData = series[seriesIndex][dataPointIndex];
        return `<div style="width: 100%; height: 100%; padding: 10px 12px;background: #F7F9F9; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18); border-radius: 4px; display: flex">
            <div style="color: #272727; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 500;">${w.globals.categoryLabels[dataPointIndex] ? (w.globals.categoryLabels[dataPointIndex].join ? w.globals.categoryLabels[dataPointIndex].join(' ') : w.globals.categoryLabels[dataPointIndex]) : (`${layer?.categories[dataPointIndex]} - ${layer?.categories[dataPointIndex + 1] || closestUpperDivisibleBy5(layer.data.max)}`)}:</div>
            <div style="color: #5E84F7; font-size: 14px; font-family: Montserrat,sans-serif; font-weight: 600; margin-left: 8px">${initData} (%)</div>
           </div>`;
      },
      y: {
        formatter(val: number): string {
          return `${val}%`;
        }
      }
    };
  }

  private blobToBase64(blob: Blob): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        observer.next(base64String);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  private checkIfAllDataDownloaded(layer: Layer) {
    if (this.layersObj[layer.layer.name].img !== undefined && this.layersObj[layer.layer.name].value !== undefined && this.layersObj[layer.layer.name].histogram !== undefined) {
      this.layersObj[layer.layer.name].status = true;
      if (Object.values(this.layersObj).every(layerData => layerData.status === true)) {
        this.indexedDbService.saveData(`${this.data.layer.coordinates[0]}, ${this.data.layer.coordinates[1]}`, this.layersObj, 'skyforest', 'quickReport');
      }
    } else {
      this.layersObj[layer.layer.name].status = false;
    }
  }

  public getLayerData() {
    return this.layerData.filter(el => el.layer?.layer.name !== 'all' && el.layer?.layer.name !== 'exposure').sort((a, b) => {
      // @ts-ignore
      if (a?.layer?.order < b?.layer?.order) {
        return -1;
        // @ts-ignore
      } else if (a?.layer?.order > b?.layer?.order) {
        return 1;
      }
      return 0;
    });
  }

  public filterLayers(): { [key: string]: DataLayerStats } {
    const resp = {};
    for (const layersKey in this.layersObj) {
      if (layersKey !== 'disttohazard' && layersKey !== 'exposure') {
        resp[layersKey] = this.layersObj[layersKey];
        const orderIndex = this.layerTabsOrder.indexOf(resp[layersKey].layer?.display_name);
        resp[layersKey].order = orderIndex === -1 ? Infinity : orderIndex;
      }
    }
    return resp;
  }

  public calculateValueLabel(value: number, layer: DataLayerStats) {
    if (layer?.categorical && layer?.categories && value !== undefined) {
      if (layer.layer.layer.name === 'landcover') value = value - 1;
      return layer.categories[value];
    } else {
      return value === null && layer?.layer?.layer.name === 'pcba' ? 'No Trees' : value;
    }
  }

  public layerLabelTransform(name: string) {
    return name?.replace(' Model', '')?.replace('PSBA', 'PCBA');
  }

  public sortValue(a: any, b: any) {
    if (+a.value.layer.order < +b.value.layer.order) {
      return -1;
    } else if (+a.value.layer.order > +b.value.layer.order) {
      return 1;
    }
    return 0;
  }

  private getFillColors(layer: Layer, dataBinsLength: number) {
    if (layer.layer.name === 'chm') dataBinsLength = 9;
    const colors = chroma.scale(this.getDiscreteColors(layer)).colors(dataBinsLength);
    if (layer.layer.value_type === 'CATEGORICAL') {
      return Array.from(Array(dataBinsLength).keys()).map((el, index) => {
        return this.getDiscreteColors(layer)[index];
      });
    } else {
      return Array.from(Array(dataBinsLength).keys()).map((el, index) => {
        return colors[index];
      });
    }
  }

  public closeDialog(result: any): void {
    this.dialogRef.close(result);
  }

  public discard() {
    this.closeDialog(true);
  }

  public save() {
    this.dialog.open(SaveAreaDialogComponent, {
      data: {
        layer: { ...this.data.layer, type: 'Point' },
        project: this.data.project
      }
    });
  }

  protected readonly isLinearLayer = isLinearLayer;
}
